"use client";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import {
  Check,
  ChevronRight,
  Loader2,
  AlertCircle,
  ShieldCheck,
  ShieldX,
  ShieldAlert,
  Wrench,
  Clock,
} from "lucide-react";
import * as React from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * All possible tool execution states.
 * Maps both AI SDK style names and our internal names.
 */
export type ToolState =
  | "input-streaming"
  | "input-available"
  | "call"
  | "output-available"
  | "result"
  | "output-error"
  | "error"
  | "approval-requested"
  | "pending_approval"
  | "approval-responded"
  | "approved"
  | "output-denied"
  | "rejected";

function normalizeState(state: ToolState): string {
  const map: Record<string, string> = {
    call: "input-available",
    result: "output-available",
    error: "output-error",
    pending_approval: "approval-requested",
    approved: "approval-responded",
    rejected: "output-denied",
  };
  return map[state] || state;
}

// ---------------------------------------------------------------------------
// Status Badge
// ---------------------------------------------------------------------------

interface StatusBadgeConfig {
  icon: React.ReactNode;
  label: string;
  className: string;
}

function getStatusConfig(state: ToolState): StatusBadgeConfig {
  const normalized = normalizeState(state);

  switch (normalized) {
    case "input-streaming":
      return {
        icon: <Clock className="h-3 w-3" />,
        label: "Pendente",
        className:
          "bg-[color:var(--card-bg)] text-[color:var(--foreground-muted)] border border-[color:var(--card-border)]",
      };
    case "input-available":
      return {
        icon: <Loader2 className="h-3 w-3 animate-spin" />,
        label: "Executando",
        className:
          "bg-[color:var(--nt-primary)]/10 text-[color:var(--nt-primary)] border border-[color:var(--nt-primary)]/20",
      };
    case "output-available":
      return {
        icon: <Check className="h-3 w-3" />,
        label: "Concluído",
        className:
          "bg-emerald-50 text-emerald-600 border border-emerald-200/60 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-800/30",
      };
    case "output-error":
      return {
        icon: <AlertCircle className="h-3 w-3" />,
        label: "Erro",
        className:
          "bg-[color:var(--icon-red)]/10 text-[color:var(--icon-red)] border border-[color:var(--icon-red)]/20",
      };
    case "approval-requested":
      return {
        icon: <ShieldAlert className="h-3 w-3" />,
        label: "Aguardando Aprovação",
        className:
          "bg-[color:var(--icon-orange)]/10 text-[color:var(--icon-orange)] border border-[color:var(--icon-orange)]/20",
      };
    case "approval-responded":
      return {
        icon: <ShieldCheck className="h-3 w-3" />,
        label: "Aprovado",
        className:
          "bg-emerald-50 text-emerald-600 border border-emerald-200/60 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-800/30",
      };
    case "output-denied":
      return {
        icon: <ShieldX className="h-3 w-3" />,
        label: "Rejeitado",
        className:
          "bg-[color:var(--icon-red)]/10 text-[color:var(--icon-red)] border border-[color:var(--icon-red)]/20",
      };
    default:
      return {
        icon: <Wrench className="h-3 w-3" />,
        label: "Ferramenta",
        className:
          "bg-[color:var(--card-bg)] text-[color:var(--foreground-muted)] border border-[color:var(--card-border)]",
      };
  }
}

export function StatusBadge({ state }: { state: ToolState }) {
  const config = getStatusConfig(state);

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium leading-tight",
        config.className
      )}
    >
      {config.icon}
      {config.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Tool Context
// ---------------------------------------------------------------------------

interface ToolContextValue {
  state: ToolState;
  isOpen: boolean;
}

const ToolContext = React.createContext<ToolContextValue | null>(null);

// ---------------------------------------------------------------------------
// <Tool />
// ---------------------------------------------------------------------------

export interface ToolProps extends React.ComponentProps<typeof Collapsible> {
  state: ToolState;
}

export function Tool({ children, className, state, ...props }: ToolProps) {
  const normalized = normalizeState(state);
  const isCompleted =
    normalized === "output-available" || normalized === "approval-responded";
  const isError = normalized === "output-error" || normalized === "output-denied";

  const [isOpen, setIsOpen] = React.useState(
    normalized === "input-available" || isCompleted || isError
  );

  React.useEffect(() => {
    if (normalized === "input-available") setIsOpen(true);
    if (isCompleted || isError) setIsOpen(true);
  }, [normalized, isCompleted, isError]);

  return (
    <ToolContext.Provider value={{ state, isOpen }}>
      <Collapsible
        open={isOpen}
        onOpenChange={setIsOpen}
        className={cn(
          "w-full rounded-xl border overflow-hidden transition-colors",
          "bg-[color:var(--chat-agent-bubble-bg)] border-[color:var(--chat-agent-bubble-border)]",
          normalized === "input-available" && "border-[color:var(--nt-primary)]/30",
          isError && "border-[color:var(--icon-red)]/30",
          normalized === "approval-requested" && "border-[color:var(--icon-orange)]/30",
          className
        )}
        {...props}
      >
        {children}
      </Collapsible>
    </ToolContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// <ToolHeader />
// ---------------------------------------------------------------------------

const INTEGRATION_ICONS: Record<string, string> = {
  clickup: "/icons/integrations/clickup.svg",
};

/** LinkedIn logo as inline SVG so we can style fill for light/dark mode */
function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

export type ToolHeaderProps = React.ComponentPropsWithoutRef<
  typeof CollapsibleTrigger
> & {
  toolName?: string;
  title?: string;
  state: ToolState;
  /** When set, shows integration icon (ClickUp / LinkedIn) instead of state icon */
  integration?: "clickup" | "linkedin";
};

export function ToolHeader({
  children,
  className,
  toolName,
  title,
  state,
  integration,
  ...props
}: ToolHeaderProps) {
  const normalized = normalizeState(state);
  const isCompleted =
    normalized === "output-available" || normalized === "approval-responded";
  const isRunning =
    normalized === "input-available" || normalized === "input-streaming";

  const displayName = title || toolName || "Ferramenta";
  const integrationIconSrc =
    integration && integration !== "linkedin"
      ? INTEGRATION_ICONS[integration]
      : null;
  const showLinkedInIcon = integration === "linkedin";

  return (
    <CollapsibleTrigger
      className={cn(
        "flex w-full items-center justify-between px-3.5 py-2.5 text-sm font-medium transition-colors group",
        "bg-[color:var(--chat-agent-bubble-bg)] hover:opacity-90",
        className
      )}
      {...props}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        {/* Integration icon or state icon */}
        <div
          className={cn(
            "flex h-6 w-6 shrink-0 items-center justify-center rounded-md overflow-hidden",
            (integrationIconSrc || showLinkedInIcon) && "bg-[color:var(--card-bg)] p-0.5",
            !integrationIconSrc && !showLinkedInIcon && isRunning && "bg-[color:var(--nt-primary)]/10",
            !integrationIconSrc && !showLinkedInIcon && isCompleted && "bg-[color:var(--card-bg)]",
            !integrationIconSrc && !showLinkedInIcon && normalized === "output-error" && "bg-[color:var(--icon-red)]/10",
            !integrationIconSrc && !showLinkedInIcon && normalized === "output-denied" && "bg-[color:var(--icon-red)]/10",
            !integrationIconSrc && !showLinkedInIcon && normalized === "approval-requested" && "bg-[color:var(--icon-orange)]/10",
            !integrationIconSrc &&
              !showLinkedInIcon &&
              !isRunning &&
              !isCompleted &&
              normalized !== "output-error" &&
              normalized !== "output-denied" &&
              normalized !== "approval-requested" &&
              "bg-[color:var(--card-bg)]"
          )}
        >
          {showLinkedInIcon ? (
            <LinkedInIcon className="h-full w-full text-[#0A66C2] dark:text-[#e5e7eb]" />
          ) : integrationIconSrc ? (
            <img
              src={integrationIconSrc}
              alt=""
              className="h-full w-full rounded object-contain"
            />
          ) : isRunning ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-[color:var(--nt-primary)]" />
          ) : isCompleted ? (
            <Check className="h-3.5 w-3.5 text-[color:var(--foreground)]" />
          ) : normalized === "output-error" || normalized === "output-denied" ? (
            <AlertCircle className="h-3.5 w-3.5 text-[color:var(--icon-red)]" />
          ) : normalized === "approval-requested" ? (
            <ShieldAlert className="h-3.5 w-3.5 text-[color:var(--icon-orange)]" />
          ) : (
            <Wrench className="h-3.5 w-3.5 text-[color:var(--foreground-muted)]" />
          )}
        </div>

        {/* Tool name */}
        <span className="truncate text-[13px] font-medium text-[color:var(--foreground)]">
          {displayName}
        </span>

        {/* Status badge */}
        <StatusBadge state={state} />
        {children}
      </div>

      <ChevronRight className="h-4 w-4 shrink-0 text-[color:var(--foreground-muted)] transition-transform duration-200 group-data-[state=open]:rotate-90" />
    </CollapsibleTrigger>
  );
}

// ---------------------------------------------------------------------------
// <ToolContent />
// ---------------------------------------------------------------------------

export function ToolContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof CollapsibleContent>) {
  return (
    <CollapsibleContent
      className={cn(
        "overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down",
        "border-t border-[color:var(--chat-agent-bubble-border)]",
        "bg-[color:var(--card-bg)]",
        className
      )}
      {...props}
    >
      <div className="p-3.5 text-sm space-y-3 text-[color:var(--foreground)]">{children}</div>
    </CollapsibleContent>
  );
}

// ---------------------------------------------------------------------------
// <ToolInput />
// ---------------------------------------------------------------------------

export function ToolInput({
  input,
  className,
}: {
  input: any;
  className?: string;
}) {
  if (!input || (typeof input === "object" && Object.keys(input).length === 0))
    return null;

  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-[color:var(--foreground-muted)]">
        Parâmetros
      </div>
      <pre className="overflow-x-auto rounded-lg bg-[color:var(--chat-agent-bubble-bg)] p-3 text-xs font-mono text-[color:var(--foreground)] border border-[color:var(--card-border)] leading-relaxed">
        {typeof input === "string" ? input : JSON.stringify(input, null, 2)}
      </pre>
    </div>
  );
}

// ---------------------------------------------------------------------------
// <ToolOutput />
// ---------------------------------------------------------------------------

export function ToolOutput({
  output,
  errorText,
  className,
}: {
  output?: any;
  errorText?: string;
  className?: string;
}) {
  if (!output && !errorText) return null;

  return (
    <div className={cn("space-y-1.5", className)}>
      <div
        className={cn(
          "text-[11px] font-semibold uppercase tracking-wider",
          errorText
            ? "text-[color:var(--icon-red)]"
            : "text-[color:var(--foreground-muted)]"
        )}
      >
        {errorText ? "Erro" : "Resultado"}
      </div>
      {errorText ? (
        <div className="rounded-lg bg-[color:var(--icon-red)]/5 p-3 text-xs text-[color:var(--icon-red)] font-mono border border-[color:var(--icon-red)]/20 leading-relaxed">
          {errorText}
        </div>
      ) : (
        <pre className="overflow-x-auto rounded-lg bg-[color:var(--chat-agent-bubble-bg)] p-3 text-xs font-mono text-[color:var(--foreground)] border border-[color:var(--card-border)] leading-relaxed max-h-64 overflow-y-auto">
          {typeof output === "string"
            ? output
            : JSON.stringify(output, null, 2)}
        </pre>
      )}
    </div>
  );
}
